"use client";

import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { useLocale } from "next-intl";
import { getTextDirection, type Locale } from "@/i18n/routing";
import { withBrandLtr } from "@/ui/home/utils/with-brand-ltr";
import Button from "@/ui/shared/components/button/button";
import { cn } from "@/ui/shared/utils/utils";
import styles from "./who-we-serve.module.css";

const GOVERNMENT_VIDEO = "/videos/who-we-serve-government.mp4";
const PRIVATE_CAPITAL_VIDEO = "/videos/who-we-serve-private-capital.mp4";
const SME_VIDEO = "/videos/who-we-serve-sme.mp4";
const BANK_VIDEO = "/videos/who-we-serve-bank.mp4";
const PRIVATE_CAPITAL_IMAGE = "/images/who-we-serve/private-capital.webp";
const DESKTOP_MQ = "(min-width: 1025px)";
const ENTRANCE_EASE = [0.16, 1, 0.3, 1] as const;

const AUDIENCES: readonly { id: string; icon: string; rotate?: boolean }[] = [
  { id: "governments", icon: "/images/who-we-serve/icon-governments.svg" },
  { id: "enterprises", icon: "/images/who-we-serve/icon-enterprises.svg" },
  { id: "suppliers", icon: "/images/who-we-serve/icon-suppliers.svg", rotate: true },
  { id: "banks", icon: "/images/who-we-serve/icon-banks.svg", rotate: true },
  { id: "capital", icon: "/images/who-we-serve/icon-capital.svg" },
];

type AudienceMedia =
  | { src: string; type: "video"; poster: string }
  | { src: string; type: "image"; poster?: undefined };

const AUDIENCE_MEDIA: Record<string, AudienceMedia> = {
  governments: {
    src: GOVERNMENT_VIDEO,
    type: "video",
    poster: "/images/who-we-serve/poster-government.webp",
  },
  enterprises: {
    src: PRIVATE_CAPITAL_VIDEO,
    type: "video",
    poster: "/images/who-we-serve/poster-enterprises.webp",
  },
  suppliers: {
    src: SME_VIDEO,
    type: "video",
    poster: "/images/who-we-serve/poster-suppliers.webp",
  },
  banks: {
    src: BANK_VIDEO,
    type: "video",
    poster: "/images/who-we-serve/poster-banks.webp",
  },
  capital: { src: PRIVATE_CAPITAL_IMAGE, type: "image" },
};

const PRELOAD_MEDIA = Array.from(
  new Map(
    Object.values(AUDIENCE_MEDIA).map((media) => [media.src, media] as const),
  ).values(),
);

const retainedAudiencePreloads: (HTMLImageElement | HTMLVideoElement)[] = [];
let audienceMediaPreloadStarted = false;
let audiencePosterPreloadStarted = false;

function preloadAudiencePosters() {
  if (typeof document === "undefined" || audiencePosterPreloadStarted) return;
  audiencePosterPreloadStarted = true;

  for (const media of PRELOAD_MEDIA) {
    const poster = media.type === "video" ? media.poster : media.src;
    const image = new window.Image();
    image.decoding = "async";
    image.fetchPriority = "low";
    image.src = poster;
    retainedAudiencePreloads.push(image);
  }
}

function preloadAudienceVideos(prioritySrc?: string) {
  if (typeof document === "undefined") return;

  const videos = PRELOAD_MEDIA.filter((media) => media.type === "video");
  const ordered = prioritySrc
    ? [
        ...videos.filter((media) => media.src === prioritySrc),
        ...videos.filter((media) => media.src !== prioritySrc),
      ]
    : videos;

  for (const media of ordered) {
    if (retainedAudiencePreloads.some((el) => el instanceof HTMLVideoElement && el.src.endsWith(media.src))) {
      continue;
    }
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.poster = media.poster;
    video.src = media.src;
    video.load();
    retainedAudiencePreloads.push(video);
  }
}

function preloadAudienceMedia(prioritySrc?: string) {
  preloadAudiencePosters();
  if (audienceMediaPreloadStarted && !prioritySrc) return;
  audienceMediaPreloadStarted = true;
  preloadAudienceVideos(prioritySrc);
}

/** Posters ASAP; videos after a short beat so hero/nav finish first. */
function scheduleAudienceMediaPreload(onStart?: () => void) {
  if (typeof window === "undefined") return () => {};

  let timeoutId = 0;
  let cancelled = false;

  // Tiny stills first — these paint instantly when the section is reached.
  preloadAudiencePosters();

  const run = () => {
    if (cancelled) return;
    preloadAudienceMedia(GOVERNMENT_VIDEO);
    onStart?.();
  };

  const schedule = () => {
    if (cancelled) return;
    timeoutId = window.setTimeout(run, 400);
  };

  if (document.readyState === "complete") {
    schedule();
  } else {
    window.addEventListener("load", schedule, { once: true });
  }

  return () => {
    cancelled = true;
    window.removeEventListener("load", schedule);
    if (timeoutId) window.clearTimeout(timeoutId);
  };
}

type ServeMediaProps = {
  audienceId: string;
  alt: string;
  className?: string;
  active?: boolean;
  warm?: boolean;
};

function ServeMedia({
  audienceId,
  alt,
  className,
  active = true,
  warm = false,
}: ServeMediaProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const media = AUDIENCE_MEDIA[audienceId] ?? {
    src: PRIVATE_CAPITAL_VIDEO,
    type: "video" as const,
    poster: "/images/who-we-serve/poster-enterprises.webp",
  };
  const shouldBuffer = warm || active;

  useEffect(() => {
    setVideoReady(false);
  }, [media.src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || media.type !== "video") return;
    if (active) {
      void video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [active, media.type]);

  // Changing preload= after mount does not always restart buffering — force load when warmed.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || media.type !== "video" || !shouldBuffer) return;
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      setVideoReady(true);
      return;
    }
    video.load();
  }, [shouldBuffer, media.type, media.src]);

  if (media.type === "image") {
    return (
      <img
        className={className}
        src={media.src}
        alt={active ? alt : ""}
        decoding="async"
        loading={shouldBuffer ? "eager" : "lazy"}
        fetchPriority={active ? "high" : "low"}
        aria-hidden={!active}
      />
    );
  }

  return (
    <span className={className ? `${className} ${styles.mediaFrame}` : styles.mediaFrame}>
      <img
        className={styles.poster}
        src={media.poster}
        alt=""
        decoding="async"
        loading="eager"
        fetchPriority={active ? "high" : "low"}
        aria-hidden
      />
      <video
        ref={videoRef}
        className={styles.video}
        src={media.src}
        poster={media.poster}
        muted
        loop
        playsInline
        autoPlay={active}
        preload={shouldBuffer ? "auto" : "metadata"}
        data-ready={videoReady ? "true" : undefined}
        aria-label={active ? alt : undefined}
        aria-hidden={!active}
        onLoadedData={() => setVideoReady(true)}
        onCanPlay={() => setVideoReady(true)}
      />
    </span>
  );
}

const panelVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.04,
    },
  },
};

const layoutVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.14 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: ENTRANCE_EASE },
  },
};

const fadeUpInstant = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0, transition: { duration: 0 } },
};

export type WhoWeServeTranslations = {
  serveTitle: string;
  serveImageAlt: string;
  serveCallout: string;
  servePrev: string;
  serveNext: string;
  serveItems: { id: string; title: string; body: string }[];
};

type ServeItem = WhoWeServeTranslations["serveItems"][number];

type WhoWeServeProps = {
  translations: WhoWeServeTranslations;
  skipAnimation?: boolean;
  className?: string;
};

type AudienceCopyProps = {
  item: ServeItem;
  icon?: string;
  rotate?: boolean;
  active?: boolean;
  collapseBody?: boolean;
};

function isDesktopViewport() {
  return window.matchMedia(DESKTOP_MQ).matches;
}

function shotTransition(skip: boolean, delay: number, duration: number) {
  return skip
    ? { duration: 0 }
    : { duration, delay, ease: ENTRANCE_EASE };
}

function CalloutLeader({
  className,
  play,
  skip,
}: {
  className?: string;
  play: boolean;
  skip: boolean;
}) {
  const shown = skip || play;

  return (
    <svg
      className={className}
      viewBox="0 0 74.3486 27.1667"
      fill="none"
      preserveAspectRatio="none"
      overflow="visible"
      aria-hidden
    >
      <motion.circle
        cx="2.66667"
        cy="2.66667"
        r="2.66667"
        fill="currentColor"
        initial={skip ? false : { opacity: 0 }}
        animate={{ opacity: shown ? 1 : 0 }}
        transition={shotTransition(skip, 0.28, 0.25)}
      />
      <motion.path
        d="M2.667 2.667 H64.667 A6 6 0 0 1 70.667 8.667 V26.667"
        stroke="currentColor"
        strokeWidth="1"
        initial={skip ? false : { pathLength: 0 }}
        animate={{ pathLength: shown ? 1 : 0 }}
        transition={shotTransition(skip, 0.32, 0.55)}
      />
      <motion.path
        d="M67.838 23.131 L70.667 26.667 L73.495 23.131"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="miter"
        initial={skip ? false : { opacity: 0 }}
        animate={{ opacity: shown ? 1 : 0 }}
        transition={shotTransition(skip, 0.82, 0.2)}
      />
    </svg>
  );
}

function AudienceCopy({
  item,
  icon,
  rotate,
  active = true,
  collapseBody = false,
}: AudienceCopyProps) {
  const collapsed = collapseBody && !active;

  return (
    <>
      <span className={styles.iconBox} aria-hidden>
        {icon ? (
          <span
            className={cn(styles.icon, rotate && styles.iconRotated)}
            style={{
              maskImage: `url(${icon})`,
              WebkitMaskImage: `url(${icon})`,
            }}
          />
        ) : null}
      </span>
      <span className={styles.itemCopy}>
        <span className={styles.itemTitle}>{item.title}</span>
        <span className={styles.itemBody} aria-hidden={collapsed} inert={collapsed}>
          <span className={styles.itemBodyInner}>
            {item.body.split("\n\n").map((paragraph, paragraphIndex) => (
              <span key={paragraphIndex} className={styles.itemPara}>
                {withBrandLtr(paragraph, styles.brandLtr)}
              </span>
            ))}
          </span>
        </span>
      </span>
    </>
  );
}

export function WhoWeServe({
  translations,
  skipAnimation = false,
  className,
}: WhoWeServeProps) {
  const locale = useLocale() as Locale;
  const dir = getTextDirection(locale);
  const reduceMotion = useReducedMotion();
  const disableEntrance = skipAnimation || Boolean(reduceMotion);
  const stickyRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedRef = useRef(0);
  const [selected, setSelected] = useState(0);
  const [isDesktop, setIsDesktop] = useState(true);
  const panelInView = useInView(stickyRef, { once: true, amount: 0.25 });
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: true,
    duration: 22,
    direction: dir,
  });

  const items = translations.serveItems;
  const count = items.length;
  selectedRef.current = selected;
  const showEntrance = disableEntrance || panelInView;
  const itemVariants = disableEntrance ? fadeUpInstant : fadeUp;
  const [mediaWarm, setMediaWarm] = useState(audienceMediaPreloadStarted);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // After other page assets finish loading; if the user reaches the section first, warm immediately.
  useEffect(() => scheduleAudienceMediaPreload(() => setMediaWarm(true)), []);

  useEffect(() => {
    if (!panelInView || mediaWarm) return;
    const activeSrc = AUDIENCE_MEDIA[AUDIENCES[selected]?.id ?? "governments"]?.src;
    preloadAudienceMedia(activeSrc);
    setMediaWarm(true);
  }, [panelInView, mediaWarm, selected]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    if (isDesktopViewport()) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    emblaApi?.reInit();
  }, [dir, emblaApi]);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const onChange = () => {
      if (mq.matches) return;
      emblaApi?.reInit();
      emblaApi?.scrollTo(selectedRef.current, true);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [emblaApi]);

  const goTo = useCallback(
    (index: number) => {
      const next = Math.max(0, Math.min(count - 1, index));
      if (isDesktopViewport()) {
        setSelected(next);
        return;
      }

      emblaApi?.scrollTo(next);
    },
    [count, emblaApi],
  );

  const goPrev = () => {
    emblaApi?.scrollPrev();
  };

  const goNext = () => {
    emblaApi?.scrollNext();
  };

  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = count - 1;
    let next = index;
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();
      next = index === last ? 0 : index + 1;
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();
      next = index === 0 ? last : index - 1;
    } else if (event.key === "Home") {
      event.preventDefault();
      next = 0;
    } else if (event.key === "End") {
      event.preventDefault();
      next = last;
    } else {
      return;
    }
    goTo(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section
      id="who-we-serve"
      className={cn(styles.section, className)}
      dir={dir}
      aria-labelledby="who-we-serve-heading"
    >
      <motion.div
        ref={stickyRef}
        className={styles.sticky}
        initial={disableEntrance ? "visible" : "hidden"}
        animate={showEntrance ? "visible" : "hidden"}
        variants={disableEntrance ? undefined : panelVariants}
      >
        <motion.div className={styles.header} variants={itemVariants}>
          <h2 id="who-we-serve-heading" className={styles.heading}>
            {translations.serveTitle}
          </h2>
          <div className={styles.controls}>
            <Button
              type="button"
              variant="accent-brand-outline"
              size="icon"
              className={styles.navButton}
              aria-label={translations.servePrev}
              onClick={goPrev}
              icon={<ChevronLeft className="rtlFlipH" aria-hidden />}
            />
            <Button
              type="button"
              variant="accent-brand-outline"
              size="icon"
              className={styles.navButton}
              aria-label={translations.serveNext}
              onClick={goNext}
              icon={<ChevronRight className="rtlFlipH" aria-hidden />}
            />
          </div>
        </motion.div>

        <motion.div
          className={styles.layout}
          variants={disableEntrance ? undefined : layoutVariants}
        >
          <motion.div
            className={styles.list}
            role="tablist"
            aria-label={translations.serveTitle}
            aria-orientation="vertical"
            variants={itemVariants}
          >
            {items.map((item, index) => {
              const active = index === selected;
              const audience = AUDIENCES[index];
              return (
                <button
                  key={item.id}
                  ref={(node) => {
                    tabRefs.current[index] = node;
                  }}
                  type="button"
                  role="tab"
                  id={`serve-tab-${item.id}`}
                  tabIndex={active ? 0 : -1}
                  aria-selected={active}
                  aria-controls={`serve-panel-${item.id}`}
                  className={cn(styles.item, active && styles.itemActive)}
                  onClick={() => goTo(index)}
                  onKeyDown={(event) => onTabKeyDown(event, index)}
                >
                  <AudienceCopy
                    item={item}
                    icon={audience?.icon}
                    rotate={audience?.rotate}
                    active={active}
                    collapseBody
                  />
                </button>
              );
            })}
          </motion.div>

          <motion.div className={styles.media} variants={itemVariants}>
            <div className={styles.stage}>
              <div className={styles.imageFrame} dir="ltr">
                <div className={styles.shotStack}>
                  <div className={styles.glow} aria-hidden />
                  <div className={styles.desktopShot}>
                    {AUDIENCES.map((audience, index) => (
                      <div
                        key={audience.id}
                        className={cn(
                          styles.mediaLayer,
                          index === selected && styles.mediaLayerActive,
                        )}
                        aria-hidden={index !== selected}
                      >
                        {isDesktop ? (
                          <ServeMedia
                            audienceId={audience.id}
                            alt={translations.serveImageAlt}
                            className={styles.image}
                            active={index === selected && showEntrance}
                            warm={mediaWarm}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
                <Fragment key={disableEntrance ? "static" : selected}>
                  <span className={styles.leader} aria-hidden>
                    <CalloutLeader
                      className={styles.leaderImg}
                      play={showEntrance}
                      skip={disableEntrance}
                    />
                  </span>
                  <motion.p
                    className={cn(styles.callout, styles.overlayCallout)}
                    initial={disableEntrance ? false : { opacity: 0 }}
                    animate={{ opacity: showEntrance ? 1 : 0 }}
                    transition={shotTransition(disableEntrance, 0.5, 0.45)}
                  >
                    {translations.serveCallout}
                  </motion.p>
                </Fragment>
                <div className={styles.viewport} ref={emblaRef}>
                  <div className={styles.container}>
                    {items.map((item, index) => {
                      const active = index === selected;
                      const audience = AUDIENCES[index];
                      return (
                        <div
                          key={item.id}
                          className={styles.slide}
                          role="tabpanel"
                          id={`serve-panel-${item.id}`}
                          aria-labelledby={`serve-tab-${item.id}`}
                          aria-hidden={!active}
                          inert={!active}
                        >
                          {!isDesktop ? (
                            <ServeMedia
                              audienceId={audience?.id ?? item.id}
                              alt={translations.serveImageAlt}
                              className={styles.image}
                              active={active && showEntrance}
                              warm={mediaWarm}
                            />
                          ) : (
                            <div className={styles.image} aria-hidden />
                          )}
                          <motion.p
                            className={cn(styles.callout, styles.slideCallout)}
                            initial={disableEntrance ? false : { opacity: 0 }}
                            animate={{ opacity: active && showEntrance ? 1 : 0 }}
                            transition={shotTransition(disableEntrance, 0.2, 0.45)}
                          >
                            {translations.serveCallout}
                          </motion.p>
                          <motion.div
                            className={cn(styles.slideCopy, styles.itemActive)}
                            initial={disableEntrance ? false : { opacity: 0 }}
                            animate={{ opacity: active && showEntrance ? 1 : 0 }}
                            transition={shotTransition(disableEntrance, 0.28, 0.45)}
                          >
                            <AudienceCopy
                              item={item}
                              icon={audience?.icon}
                              rotate={audience?.rotate}
                            />
                          </motion.div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}
